<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Discount Schemes</h1>
                <v-btn color="black" prepend-icon="mdi-plus" @click="dialogSwitch = true">Add</v-btn>
            </v-col>
        </v-row>
        <h3></h3>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Discount Type</th>
                        <th>Discount Value</th>
                        <th>Duration Months</th>
                        <th>Max Total Discount</th>
                        <th>Max Total Redemptions</th>
                        <th>Valid From</th>
                        <th>Valid Until</th>
                        <th>Status</th>
                        <th>Created At</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="discount_scheme in discount_schemes" v-if="discount_schemes.length"
                        :key="discount_scheme.id" class="text-left">
                        <td>{{ discount_scheme.name }}</td>
                        <td>{{ getSchemeType(discount_scheme.discount_type) }}</td>
                        <td>{{ discount_scheme.discount_value }}</td>
                        <td>{{ discount_scheme.duration_months }}</td>
                        <td>{{ discount_scheme.max_total_discount }}</td>
                        <td>{{ discount_scheme.max_total_redemptions }}</td>
                        <td>{{ new Date(discount_scheme.valid_from).toLocaleDateString() }}</td>
                        <td>{{ new Date(discount_scheme.valid_until).toLocaleDateString() }}</td>
                        <td>
                            <v-icon :icon="discount_scheme.is_active?'mdi-check-circle':'mdi-close-circle'"/>
                        </td>
                        <td>{{ formatDateTime(discount_scheme.created_at) }}</td>
                        <td>
                            <v-btn-group density="compact" divided variant="outlined">
                                <v-btn :disabled="editDialogSwitch" class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small" @click="editDiscountScheme(discount_scheme)">Edit
                                </v-btn>
                                <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                       @click="deleteDiscountScheme(discount_scheme)">Delete
                                </v-btn>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="9">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="9">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination
                    v-model="currentPage"
                    :length="totalPages"
                    class="my-4"
                    density="compact"
                    @update:model-value="fetchDiscountSchemes"
                ></v-pagination>
            </v-col>
        </v-row>
    </v-container>

    <create-edit-discount-scheme v-if="dialogSwitch" v-model="dialogSwitch"
                                 :discount_scheme="selectedDiscountScheme"
                                 @addDiscountScheme="addDiscountScheme"
                                 @updateDiscountScheme="updateDiscountScheme"/>
</template>

<script setup>
import {onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import CreateEditDiscountScheme from "@/components/dialogs/discount-schemes/Create_Edit.vue";

const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const discount_schemes = ref([]);
const {showSnackbar} = useSnackbarStore();
const dialogSwitch = ref(false);
const editDialogSwitch = ref(false);
const selectedDiscountScheme = ref(null);
// Kept COMPLETE, including Account Credit. This list is the label lookup for
// the table below, so dropping an entry would make getSchemeType read .title off
// undefined for any scheme already stored with that type.
const schemeTypes = [
    {value: 1, title: 'Percent Off'},
    {value: 2, title: 'Fixed Monthly'},
    {value: 3, title: 'Account Credit'},
    {value: 4, title: 'Free Months'},
    {value: 5, title: 'Fixed One Time'},
];

const getSchemeType = (_type) => {
    // Optional chaining: a scheme type the UI does not know about must render as
    // its raw value, not blow the whole table up with a TypeError.
    return schemeTypes.find((type) => type.value === _type)?.title ?? _type
}
onMounted(() => {
    fetchDiscountSchemes();
});
const fetchDiscountSchemes = () => {
    send('get', `admin/discount-schemes`)
        .then((response) => {
            discount_schemes.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Discount schemes could not be loaded', 'error')
        })
}
const addDiscountScheme = (discount_scheme) => {
    discount_schemes.value.unshift(discount_scheme);
}
const editDiscountScheme = (discount_scheme) => {
    selectedDiscountScheme.value = discount_scheme;
    dialogSwitch.value = true;
}
const updateDiscountScheme = (discount_scheme) => {
    selectedDiscountScheme.value = null;
    discount_schemes.value.map((item, index) => {
        if (item.id === discount_scheme.id) {
            discount_schemes.value[index] = discount_scheme;
        }
    });
}
const deleteDiscountScheme = (discount_scheme) => {
    if (confirm(`Are you sure to delete "${discount_scheme.name}" discount scheme?`)) {
        send('delete', `admin/discount-schemes/${discount_scheme.id}`)
            .then((response) => {
                if (response.data.success) {
                    discount_schemes.value.map((item, index) => {
                        if (item.id === discount_scheme.id) {
                            discount_schemes.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${discount_scheme.name}" discount scheme could not be deleted`, 'error')
                }
            })
    }
}
</script>
